/**
 * Runs the load steps (src/ui/logic/loadPlan.ts): times each one (console "[load]" lines and performance marks /
 * measures, read by scripts/loadtrace.mjs), moves the loading bar at each step's start and end, and gives up on a step
 * that does not finish within its timeout with a LoadStalled error naming it, instead of an endless loading screen.
 * Idempotent steps (downloads) can be retried once before giving up.
 */
import { BarMove, LoadBarPlan, LoadStepId, StepRunner } from '../ui/logic/loadPlan';

export class LoadStalled extends Error {
  constructor(readonly step: LoadStepId, readonly ms: number) {
    super(`loading step "${step}" did not finish within ${Math.round(ms / 1000)} s`);
    this.name = 'LoadStalled';
  }
}

export class LoadTracker {
  readonly plan = new LoadBarPlan();
  readonly times: Partial<Record<LoadStepId, number>> = {};
  private readonly t0 = performance.now();

  constructor(private readonly bar: (m: BarMove) => void) {}

  /** Runs one step with its timeout (per attempt) and retries. */
  readonly step: StepRunner = async (id, fn, o = {}) => {
    const def = this.plan.def(id);
    this.bar(this.plan.start(id));
    const start = performance.now();
    performance.mark(`load:${id}:start`);
    console.info(`[load] ${id} start at ${Math.round(start - this.t0)} ms`);
    let lastAt = 0;
    const progress = (f: number) => { // at most ~10 bar moves a second
      const now = performance.now();
      if (now - lastAt >= 100) { lastAt = now; this.bar(this.plan.at(id, f)); }
    };
    for (let attempt = 0; ; attempt++) {
      let timer = 0;
      try {
        const out = await Promise.race([
          Promise.resolve().then(() => fn(progress)),
          new Promise<never>((_, no) => { timer = window.setTimeout(() => no(new LoadStalled(id, def.timeoutMs)), def.timeoutMs); }),
        ]);
        const ms = performance.now() - start;
        this.times[id] = ms;
        performance.measure(`load:${id}`, `load:${id}:start`);
        console.info(`[load] ${id} done in ${Math.round(ms)} ms (at ${Math.round(performance.now() - this.t0)} ms)`);
        this.bar(this.plan.finish(id));
        return out;
      } catch (err) {
        if (attempt < (o.retries ?? 0)) { console.warn(`[load] ${id} ${err instanceof LoadStalled ? 'stalled' : 'failed'}, retrying (attempt ${attempt + 2})`, err); continue; }
        if (o.optional) {
          console.warn(`[load] ${id} ${err instanceof LoadStalled ? 'stalled' : 'failed'} after ${Math.round(performance.now() - start)} ms; optional, going on`, err);
          this.bar(this.plan.finish(id));
          return undefined as never;
        }
        console.error(`[load] ${id} failed after ${Math.round(performance.now() - start)} ms`, err);
        throw err;
      } finally { clearTimeout(timer); }
    }
  };

  /** Total load time so far (ms). */
  get elapsed(): number { return performance.now() - this.t0; }
}
